import React, { useContext, useMemo } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { useIsMounted } from '@bingads-webui-campaign-react/step-wizard-controls';
import { BudgetPanel, BidStrategyPanel, BidValuePanel, PANEL_KEYS } from '@bingads-webui-campaign-react/wizard-shared-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  FormErrorGroup,
  UnmappedFormErrors as OriginalUnmappedFormErrors,
} from '@bingads-webui-campaign-react/form-error';
import {
  apiErrorMapper,
} from '@bingads-webui-campaign-react/perf-max-step-wizard';
import {
  withControlErrorHandling,
} from '@bingads-webui-campaign-react/error-handling';
import { useFormService } from '@bingads-webui-react/form';
import { StepHeader } from '@bingads-webui-campaign-react/pmax-common';
import { CampaignValidationGroup, SystemErrors, FLOWS } from '@bingads-webui-campaign-react/unified-app-shared';
import {
  Collapse,
  useCollapseState,
  collapseExpander,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { MANUAL_CPA, MANUAL_CPC } from '@bingads-webui-campaign-react/bid-strategy-component';
import { useWizardService } from '@bingads-webui-react/wizard';

import { useBudgetStepStyles } from './use-budget-step-styles';

const UnmappedFormErrors = withControlErrorHandling(OriginalUnmappedFormErrors);

export const BudgetStepShell = ({
  data: budgetInfo,
  onDataChange,
  error,
}) => {
  const i18n = useContext(I18nContext);
  const isMounted = useIsMounted();
  const { state } = useFormService();
  const { state: wizardState } = useWizardService();
  const { saveFailed } = state.context;
  const isLoading = useMemo(() => _.isEmpty(budgetInfo), [budgetInfo]);

  const getStyles = useBudgetStepStyles();

  const { serverErrors, systemErrors } = error || {};

  const [
    allSections,
    allExpandedSections,
    setExpandedSections,
  ] = useCollapseState(
    [PANEL_KEYS.BID_STRATEGY, PANEL_KEYS.BUDGET, PANEL_KEYS.BID_VALUE],
    [PANEL_KEYS.BUDGET, PANEL_KEYS.BID_VALUE]
  );

  const expandSections = useMemo(() =>
    collapseExpander({ setActiveKeys: setExpandedSections }), [setExpandedSections]);

  const {
    wizardData: { flow },
  } = wizardState.context;

  const isBidStrategyCPC = useMemo(() => flow !== FLOWS.WINDOWS, [flow]);
  const getBidStrategyOptions = useMemo(() => (isBidStrategyCPC ? [MANUAL_CPC] : [MANUAL_CPA]), [isBidStrategyCPC]);

  return (
    <ActuationGroup actuationProps={{ isActive: saveFailed }}>
      <AttentionBeaconGroup>
        <div className={classNames(getStyles('formContainer'), 'budget-step-container')}>
          <FormErrorGroup formErrorProps={{ errors: serverErrors, saveFailed, errorMapper: apiErrorMapper }}>
            <StepHeader
              title={_TL_('How much do you want to spend on this campaign?')}
              description={_TL_('Tell us about your campaign and how much you want to spend')}
            />
            <UnmappedFormErrors className="other-server-errors" />
            <SystemErrors errors={systemErrors} />
            <CampaignValidationGroup
              data={budgetInfo}
              onDataChange={onDataChange}
              isValidating={isMounted.current}
              i18n={i18n}
            >
              <Collapse
                allPanelItems={allSections}
                skipItemsTypeCheck
                activeKeys={allExpandedSections}
                setActiveKeys={setExpandedSections}
                checkItems
              >
                <BidStrategyPanel
                  key={PANEL_KEYS.BID_STRATEGY}
                  isLoading={isLoading}
                  expand={expandSections}
                  onDataChange={onDataChange}
                  data={budgetInfo}
                  options={{
                    bidStrategyComponentOptions: {
                      bidStrategyOptions: getBidStrategyOptions,
                      hideAdditionalDetails: true,
                    },
                    biddingBannerOptions: {
                      isCampaignCreation: true,
                    },
                  }}
                  topicKey="pop_BA_Apps_BidStrategy"
                />
                <BidValuePanel
                  key={PANEL_KEYS.BID_VALUE}
                  bidValueType={isBidStrategyCPC ? 'ManualCPC' : 'ManualCPI'}
                  isLoading={isLoading}
                  expand={expandSections}
                  onDataChange={onDataChange}
                  data={budgetInfo}
                />
                <BudgetPanel
                  key={PANEL_KEYS.BUDGET}
                  isLoading={isLoading}
                  expand={expandSections}
                  onDataChange={onDataChange}
                  data={budgetInfo}
                />
              </Collapse>
            </CampaignValidationGroup>
          </FormErrorGroup>
        </div>
      </AttentionBeaconGroup>
    </ActuationGroup>
  );
};

BudgetStepShell.propTypes = {
  data: PropTypes.shape({}),
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.shape({
    serverErrors: PropTypes.arrayOf(PropTypes.shape({})),
    systemErrors: PropTypes.arrayOf(PropTypes.shape({})),
  }),
};

BudgetStepShell.defaultProps = {
  data: {},
  error: {},
};
